import React, { PureComponent } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { get } from "lodash"
import { Link } from "react-router-dom"

import Loader from "components/Loader/Loader"
import ContentBox from "components/ContentBox/ContentBox"
import PageNotFound from "containers/PageErrors/PageNotFound"

import { getProgram } from "actions/programs"
import { getEpisode } from "actions/episodes"

import { selectMetadata, selectProgram } from "selectors/programs"
import { selectReferencesModel } from "selectors/references"
import { selectQuery } from "selectors/location"

import consts from "consts"
import routeHelpers from "helpers/route"

import { restoreDefaultMeta, updateMeta } from "helpers/meta"
import FormatterHelper from "helpers/formatter"
import { getAPIEnv } from "helpers/api"

import styles from "./pageProgram.css"
import { generateCanonicalLink, GetLanding } from "./PageProgramHelpers"

import PageProgramView from "./PageProgramView"
import { LOCALE } from "consts/locale"

@connect((state, props) => {
  const query = selectQuery(props)

  const selectorsParams = {
    location: props.location,
    params: props.match.params,
    query,
  }

  return {
    metadata: selectMetadata(state, selectorsParams),
    params: props.match.params,
    program: selectProgram(state, selectorsParams),
    query,
    references: selectReferencesModel(state),
    seo: state.seo,
  }
})
export default class PageProgram extends PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired,
    program: PropTypes.object.isRequired,
    references: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    metadata: PropTypes.object.isRequired,
    seo: PropTypes.object.isRequired,
    downloadCampaign: PropTypes.string,
    downloadAdGroup: PropTypes.string,
    query: PropTypes.object.isRequired,
    setSignup: PropTypes.func,
  }

  static cache = consts.cache.defaultCacheMaxAge

  static mandatoryParams = ["id", "channel_id"]

  static fetchData(dispatch, state, renderProps) {
    let channelId
    let programId
    let episodeId
    let isEpisode = false

    if (renderProps.location.pathname === "/deeplink") {
      channelId = renderProps.query.channel_id
      programId = renderProps.query.id

      if (renderProps.query.type === consts.deepLinkTypes.EPISODE) {
        programId = renderProps.query.program_id
        episodeId = renderProps.query.id
        isEpisode = true
      }
    } else {
      channelId = renderProps.params.channelId
      programId = renderProps.params.programId
    }

    const action = isEpisode ? getEpisode(channelId, episodeId) : getProgram(channelId, programId)

    return dispatch(action)
  }

  static getMeta(state, renderProps, formatted = false) {
    let isDeeplinkPage = false
    const { location, query, host } = renderProps

    if (location.pathname === "/deeplink") {
      isDeeplinkPage = true
    }

    let program
    let metadata

    if (formatted) {
      program = state.program
      metadata = state.metadata
    } else {
      const selectorsParams = {
        location: location,
        params: renderProps.params,
        query: renderProps.query,
      }
      program = selectProgram(state, selectorsParams)
      metadata = selectMetadata(state, selectorsParams)
    }

    if (program.pending || program.error) {
      return
    }
    const channel = program.getChannel()
    const programId = program.isEpisode() ? program.getProgramId() : program.getId()

    if (!channel || !channel.isValid()) {
      return
    }

    let description = `${program.getTitle()} sur ${channel.getTitle()}: Regardez ce programme sur Molotov, l'app gratuite pour regarder la TV en direct et en replay`

    if (!program.isAvailable()) {
      description = program.isAvailable()
        ? `${program.getTitle()} sur ${channel.getTitle()} : Regardez ce programme sur Molotov, l'app gratuite pour regarder la TV en direct et en replay.`
        : `Découvrez ${program.getTitle()} sur Molotov, l'app gratuite pour regarder la TV en direct et en replay`
    }
    if (isDeeplinkPage) {
      description = get(program, "data.description", "")
    }

    const appDeeplinkUri = routeHelpers.getDeeplinkUriFromProgram(program, query)

    const canonicalLink = generateCanonicalLink({
      locale: LOCALE,
      programId,
      channelId: channel.getId(),
      programSlug: program.getSlug(),
    })

    let canonicalMeta = {
      canonical: canonicalLink,
    }

    canonicalMeta.__noCanonicalRedirect = true // FORCE REDIRECT VALUE

    if (metadata.canonical || metadata.redirect) {
      const data = metadata.canonical || metadata.redirect

      canonicalMeta = {
        canonical: `${GetLanding()}${FormatterHelper.basic(consts.routes.program, {
          locale: LOCALE,
          programId: data.program_id,
          channelId: data.channel_id,
          programSlug: program.getSlug(),
        })}`,
        __noCanonicalRedirect: !!metadata.canonical, // prevent redirect if we have a canonical from the API
      }

      if (metadata.redirect) {
        canonicalMeta.__canonicalHttpStatus = 301
      }
    }

    const { gotPrograms, mythoPrograms, pblvSecondaryPrograms, pblvMainProgram, adultSwimPrograms, westworldProgram } = consts.programs

    if (gotPrograms.includes(programId)) {
      canonicalMeta.canonical = `${consts.externalLink.landing}${consts.routes.got}`
      canonicalMeta.__noCanonicalRedirect = true
    }

    if (mythoPrograms.includes(programId)) {
      canonicalMeta.canonical = `${consts.externalLink.landing}${consts.routes.mytho}`
      canonicalMeta.__noCanonicalRedirect = true
    }

    if (pblvSecondaryPrograms.includes(programId)) {
      canonicalMeta.canonical = `${consts.externalLink.landing}${routeHelpers.getSEOUri(pblvMainProgram)}`
      canonicalMeta.__noCanonicalRedirect = true
    }

    if (westworldProgram.includes(programId)) {
      canonicalMeta.canonical = `${consts.externalLink.landing}${consts.routes.westworld}`
      canonicalMeta.__noCanonicalRedirect = true
    }

    if (adultSwimPrograms.find(program => program.slug === "rick-et-morty").id === programId) {
      canonicalMeta.canonical = `${consts.externalLink.landing}${consts.routes.rickMorty}`
      canonicalMeta.__noCanonicalRedirect = true
    }

    if (!program) {
      return
    }

    const programImage = `https://fusion.molotov.tv/arts/sharing/program/${channel.data.id}/${program.data.id}`

    const apiEnv = getAPIEnv(consts.config.apiPath)

    const getRobotsRules = () => {
      let landing = GetLanding()
      const baseUrl = `${landing}${location.pathname}`

      if (apiEnv === "prod") {
        if (baseUrl === canonicalMeta?.canonical && !host?.includes("callback")) {
          return "index, follow"
        } else {
          return "noindex"
        }
      } else {
        return "noindex"
      }
    }

    const isCharged = !channel.isAvailableInFreeBundles(get(state.seo, "settings.tv_bundles_visible", []))

    //used for browser tab title
    const getMetaTitle = program => {
      const channel = program.getChannel()
      return [
        program.getTitle(),
        program.hasReplay() ? "en streaming & replay" : "en streaming",
        !isCharged ? "gratuit" : "",
        program.isAvailable() || channel.isCommercialized() ? `sur ${channel.getTitle()}` : "",
      ]
        .filter(Boolean)
        .join(" ")
    }

    return {
      title: getMetaTitle(program),
      description,
      locale: LOCALE,
      og: {
        title: `${program.getTitle()} en streaming & replay sur ${channel.getTitle()}`,
        type: "website",
        description: `Téléchargez gratuitement l'application Molotov TV afin de regarder ${program.getTitle()} en direct et en replay`,
        imageAlt: `${channel.getTitle()} en streaming et replay`,
        url: canonicalMeta.canonical,
        image: programImage,
      },
      appLinks: {
        nativeUri: appDeeplinkUri,
        webUri: appDeeplinkUri.replace(consts.deeplinkScheme, "https://"),
      },
      robots: getRobotsRules(),
      ...canonicalMeta,
    }
  }

  static getHttpStatus(state, renderProps) {
    const program = selectProgram(state, {
      location: renderProps.location,
      params: renderProps.params,
      query: renderProps.query,
    })

    if (program.error) {
      return { code: program.error.code }
    }

    if (!program || !program.getChannel() || !program.getChannel().isValid()) {
      let metaRedirect = program.getProgramRedirectMetadata()
      let metaCanonical = program.getProgramCanonicalMetadata()

      if (metaRedirect.channel_id !== program.getChannel()) {
        let url = "/fr_fr/p/" + metaRedirect.program_id + "-" + metaRedirect.channel_id + "/" + program.getSlug()
        return { code: 302, redirect: url, type: "redirect" }
      }

      if (metaCanonical.channel_id !== program.getChannel()) {
        let url = "/fr_fr/p/" + metaCanonical.program_id + "-" + metaCanonical.channel_id + "/" + program.getSlug()
        return { code: 302, redirect: url, type: "canonical" }
      }

      return { code: 410 }
    }

    return {
      code: 200,
    }
  }

  static getHeadContent(state, renderProps) {
    const program = selectProgram(state, {
      location: renderProps.location,
      params: renderProps.params,
      query: renderProps.query,
    })

    if (program.getSEORanking() === "1") {
      return <meta name="robots" content="noindex, nofollow" />
    }

    return null
  }

  programId(programId, channelId) {
    return `${programId}-${channelId}`
  }

  getTagTitle = children => {
    if (consts.programs.pblvMainProgram.id === this.props.program.data.id) {
      const newChildren = children.replace(this.props.program.data.title, "")
      return (
        <div className={styles.programDetailsSectionTitle}>
          <h1>{this.props.program.data.title}</h1> {newChildren && newChildren.toLowerCase()}
        </div>
      )
    }
    return <h2>{children}</h2>
  }

  componentDidMount() {
    if (!global.serverRendered) {
      this.constructor.fetchData(this.props.dispatch, this.props, this.props)
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.params.programId !== this.props.params.programId) {
      this.constructor.fetchData(this.props.dispatch, this.props, this.props)
    }

    if (prevProps.program.pending && !this.props.program.pending) {
      updateMeta(this.constructor.getMeta(this.props, this.props, true))
    }
  }

  componentWillUnmount() {
    restoreDefaultMeta()
  }

  render() {
    const { program } = this.props

    if (program.pending) {
      return <Loader centerInParent={true} />
    }

    if (program.error) {
      return (
        <ContentBox className={styles.errorBox}>
          <h2 className={styles.errorTitle}>Le programme que vous recherchez semble introuvable.</h2>
          <Link className={styles.errorLink} to={FormatterHelper.basic(consts.routes.seoHome, { locale: LOCALE })}>
            Retourner au programme TV
          </Link>
        </ContentBox>
      )
    }

    const channel = program.getChannel()

    if (!channel || !channel.isValid()) {
      return <PageNotFound />
    }

    return <PageProgramView program={program} />
  }
}
